import { supabase } from './supabase';
import { toAppError } from './errors';
import { cacheGet, cacheList, cachePut, cachePutMany } from './db';

/**
 * Camada de repositório: o ÚNICO lugar que fala com o Supabase para leitura.
 * Componentes React nunca montam query direto.
 *
 * Regra: lista nunca hidrata objeto pesado. Projeção leve + paginação e filtro
 * no servidor (SKILL §6, §1.5).
 */

export interface Membership {
  organization_id: string;
  role_key: string;
  role_name: string;
  organization_name: string;
  permissions: string[];
}

export interface AssetListItem {
  id: string;
  tag: string | null;
  name: string;
  inventory_number: string | null;
  asset_type_name: string | null;
  unit_name: string | null;
  sector_name: string | null;
  status_key: string | null;
  status_name: string | null;
  criticality: number | null;
  thumbnail_path: string | null;
  open_findings: number;
  total_count: number;
}

export interface PagedResult<T> {
  rows: T[];
  total: number;
  fromCache: boolean;
  fetchedAt: number;
}

async function unwrap<T>(p: PromiseLike<{ data: T | null; error: unknown }>): Promise<T> {
  const { data, error } = await p;
  if (error) throw toAppError(error);
  return data as T;
}

// ------------------------------------------------------------------ auth/tenancy
export async function loadMemberships(): Promise<Membership[]> {
  const rows = await unwrap(
    supabase
      .from('organization_memberships')
      .select(
        'organization_id, organizations!inner(legal_name, trade_name), roles!inner(key, name, role_permissions(permissions(key)))',
      )
      .eq('status', 'active'),
  );

  type Row = {
    organization_id: string;
    organizations: { legal_name: string; trade_name: string | null };
    roles: { key: string; name: string; role_permissions: { permissions: { key: string } }[] };
  };

  return (rows as unknown as Row[]).map((r) => ({
    organization_id: r.organization_id,
    role_key: r.roles.key,
    role_name: r.roles.name,
    organization_name: r.organizations.trade_name || r.organizations.legal_name,
    permissions: r.roles.role_permissions.map((rp) => rp.permissions.key),
  }));
}

export async function createOrganization(input: {
  legalName: string;
  tradeName?: string;
  cnpj?: string;
}): Promise<string> {
  return unwrap(
    supabase.rpc('app_create_organization', {
      p_legal_name: input.legalName,
      p_trade_name: input.tradeName ?? null,
      p_cnpj: input.cnpj ?? null,
    }),
  );
}

// ------------------------------------------------------------------ estrutura
export interface NamedRow { id: string; name: string }

export async function listUnits(orgId: string): Promise<NamedRow[]> {
  return unwrap(
    supabase
      .from('units')
      .select('id, name')
      .eq('organization_id', orgId)
      .eq('is_active', true)
      .order('name'),
  );
}

export async function listSectors(orgId: string, unitId?: string): Promise<NamedRow[]> {
  let q = supabase
    .from('sectors')
    .select('id, name')
    .eq('organization_id', orgId)
    .eq('is_active', true);
  if (unitId) q = q.eq('unit_id', unitId);
  return unwrap(q.order('name'));
}

export async function createUnit(orgId: string, name: string): Promise<NamedRow> {
  return unwrap(
    supabase.from('units').insert({ organization_id: orgId, name }).select('id, name').single(),
  );
}

export async function createSector(orgId: string, unitId: string, name: string): Promise<NamedRow> {
  return unwrap(
    supabase
      .from('sectors')
      .insert({ organization_id: orgId, unit_id: unitId, name })
      .select('id, name')
      .single(),
  );
}

// ------------------------------------------------------------------ ativos
/**
 * Busca paginada via RPC. Devolve APENAS o que a linha precisa: nada de fotos
 * em tamanho cheio, PDFs ou histórico completo.
 */
export async function searchAssets(params: {
  orgId: string;
  query?: string;
  unitId?: string | null;
  sectorId?: string | null;
  statusId?: string | null;
  /** recorte do que está incompleto: sem_setor, sem_tag, sem_foto, sem_inspecao, sem_apreciacao */
  pendencia?: string | null;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<PagedResult<AssetListItem>> {
  try {
    const rows = await unwrap(
      supabase
        .rpc('app_search_assets', {
          p_org: params.orgId,
          p_query: params.query || null,
          p_unit_id: params.unitId ?? null,
          p_sector_id: params.sectorId ?? null,
          p_status_id: params.statusId ?? null,
          p_limit: params.limit ?? 50,
          p_offset: params.offset ?? 0,
          p_pendencia: params.pendencia || null,
        })
        .abortSignal(params.signal as AbortSignal),
    );
    const list = (rows ?? []) as AssetListItem[];
    const fetchedAt = Date.now();

    // Cache de leitura para uso em campo, em UMA transação.
    // Não bloqueia o retorno: a lista já pode renderizar.
    void cachePutMany(
      list.map((r) => ({
        entity_type: 'asset_list_item',
        id: r.id,
        organization_id: params.orgId,
        data: r,
        fetched_at: fetchedAt,
        server_version: null,
      })),
    ).catch(() => undefined);

    return { rows: list, total: list[0]?.total_count ?? 0, fromCache: false, fetchedAt };
  } catch (err) {
    const appErr = toAppError(err);
    if (appErr.kind !== 'network') throw appErr;

    // offline: serve do cache, MARCADO como cache (carimbo de frescor na UI)
    const cached = await cacheList('asset_list_item', params.orgId);
    const q = (params.query ?? '').toLowerCase();
    const rows = cached
      .map((c) => c.data as AssetListItem)
      .filter(
        (r) =>
          !q ||
          r.name.toLowerCase().includes(q) ||
          (r.tag ?? '').toLowerCase().includes(q) ||
          (r.inventory_number ?? '').toLowerCase().includes(q),
      );
    return {
      rows,
      total: rows.length,
      fromCache: true,
      fetchedAt: Math.max(0, ...cached.map((c) => c.fetched_at)),
    };
  }
}

export interface AssetDetail {
  id: string;
  organization_id: string;
  tag: string | null;
  name: string;
  inventory_number: string | null;
  manufacturer: string | null;
  model: string | null;
  serial_number: string | null;
  manufacture_year: number | null;
  operational_capacity: string | null;
  criticality: number | null;
  unit_id: string;
  sector_id: string | null;
  notes: string | null;
  version: number;
}

export async function getAsset(id: string): Promise<AssetDetail> {
  return unwrap(
    supabase
      .from('assets')
      .select(
        'id, organization_id, tag, name, inventory_number, manufacturer, model, serial_number, manufacture_year, operational_capacity, criticality, unit_id, sector_id, notes, version',
      )
      .eq('id', id)
      .single(),
  );
}

export async function createAsset(input: {
  orgId: string;
  unitId: string;
  sectorId?: string | null;
  name: string;
  tag?: string | null;
  inventoryNumber?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  manufactureYear?: number | null;
  operationalCapacity?: string | null;
  criticality?: number | null;
}): Promise<{ id: string }> {
  return unwrap(
    supabase
      .from('assets')
      .insert({
        organization_id: input.orgId,
        unit_id: input.unitId,
        sector_id: input.sectorId || null,
        name: input.name,
        tag: input.tag || null,
        inventory_number: input.inventoryNumber || null,
        manufacturer: input.manufacturer || null,
        model: input.model || null,
        serial_number: input.serialNumber || null,
        manufacture_year: input.manufactureYear ?? null,
        operational_capacity: input.operationalCapacity || null,
        criticality: input.criticality ?? null,
      })
      .select('id')
      .single(),
  );
}

// ------------------------------------------------------------------ projetos
export async function listProjects(orgId: string): Promise<{ id: string; name: string; number: string | null }[]> {
  return unwrap(
    supabase
      .from('projects')
      .select('id, name, number')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false })
      .limit(100),
  );
}

export async function createProject(input: {
  orgId: string;
  name: string;
  number?: string | null;
  unitId?: string | null;
  scope?: string;
}): Promise<{ id: string }> {
  return unwrap(
    supabase
      .from('projects')
      .insert({
        organization_id: input.orgId,
        name: input.name,
        number: input.number || null,
        unit_id: input.unitId || null,
        scope: input.scope ?? 'nr12',
      })
      .select('id')
      .single(),
  );
}

// ------------------------------------------------------------------ formulários
export interface FormQuestion {
  id: string;
  code: string | null;
  label: string;
  /** Explicação em linguagem simples — é o que a inspeção de campo mostra
   *  embaixo da pergunta para quem não convive com a norma. */
  help: string | null;
  input_type: string;
  required: boolean;
  sort: number;
  nr_reference: string | null;
  hazard_zone: string | null;
  hazard_risk: string | null;
  default_nonconformity: string | null;
  default_recommendation: string | null;
  config: Record<string, unknown>;
}

export interface FormSection {
  id: string;
  code: string | null;
  title: string;
  nr_reference: string | null;
  sort: number;
  questions: FormQuestion[];
}

export interface FormVersion {
  id: string;
  version: number;
  template_key: string;
  template_name: string;
  kind: string;
  normative_reference: unknown;
  sections: FormSection[];
}

/** Carrega a versão PUBLICADA mais recente de um template. */
export async function loadPublishedForm(templateKey: string): Promise<FormVersion> {
  const tpl = await unwrap(
    supabase
      .from('form_templates')
      .select('id, key, name, kind')
      .eq('key', templateKey)
      .is('organization_id', null)
      .single(),
  );

  const ver = await unwrap(
    supabase
      .from('form_template_versions')
      .select('id, version, normative_reference')
      .eq('form_template_id', (tpl as unknown as { id: string }).id)
      .eq('status', 'published')
      .order('version', { ascending: false })
      .limit(1)
      .single(),
  );

  const sections = await unwrap(
    supabase
      .from('form_sections')
      .select(
        'id, code, title, nr_reference, sort, form_questions(id, code, label, help, input_type, required, sort, nr_reference, hazard_zone, hazard_risk, default_nonconformity, default_recommendation, config)',
      )
      .eq('form_template_version_id', (ver as unknown as { id: string }).id)
      .order('sort'),
  );

  type SecRow = Omit<FormSection, 'questions'> & { form_questions: FormQuestion[] };
  const t = tpl as unknown as { key: string; name: string; kind: string };
  const v = ver as unknown as { id: string; version: number; normative_reference: unknown };

  return {
    id: v.id,
    version: v.version,
    template_key: t.key,
    template_name: t.name,
    kind: t.kind,
    normative_reference: v.normative_reference,
    sections: (sections as unknown as SecRow[]).map((s) => ({
      id: s.id,
      code: s.code,
      title: s.title,
      nr_reference: s.nr_reference,
      sort: s.sort,
      questions: [...s.form_questions].sort((a, b) => a.sort - b.sort),
    })),
  };
}

// ------------------------------------------------------------------ metodologias
export interface MethodologyRow {
  id: string;
  version: string;
  status: string;
  requires_technical_approval: boolean;
  approved_at: string | null;
  definition: unknown;
  risk_methodologies: { key: string; name: string; kind: string };
}

export async function loadMethodologies(): Promise<MethodologyRow[]> {
  return unwrap(
    supabase
      .from('risk_methodology_versions')
      .select(
        'id, version, status, requires_technical_approval, approved_at, definition, risk_methodologies!inner(key, name, kind)',
      )
      .eq('status', 'published'),
  ) as unknown as Promise<MethodologyRow[]>;
}

// ------------------------------------------------------------------ campanhas
export async function createCampaign(input: {
  orgId: string;
  projectId?: string | null;
  name: string;
  kind: string;
}): Promise<{ id: string }> {
  const status = await unwrap(
    supabase.from('campaign_statuses').select('id').eq('key', 'em_campo').single(),
  );
  return unwrap(
    supabase
      .from('inspection_campaigns')
      .insert({
        organization_id: input.orgId,
        project_id: input.projectId || null,
        name: input.name,
        kind: input.kind,
        status_id: (status as unknown as { id: string }).id,
        started_on: new Date().toISOString().slice(0, 10),
      })
      .select('id')
      .single(),
  );
}

// ------------------------------------------------------------------ risco
export async function createRiskAssessment(input: {
  orgId: string;
  runId: string;
  assetId?: string | null;
  projectId?: string | null;
}): Promise<{ id: string }> {
  return unwrap(
    supabase
      .from('risk_assessments')
      .insert({
        organization_id: input.orgId,
        inspection_run_id: input.runId,
        asset_id: input.assetId || null,
        project_id: input.projectId || null,
      })
      .select('id')
      .single(),
  );
}

export interface RiskEvaluationRow {
  id: string;
  hazard_zone: string;
  hazard_risk: string | null;
  nr_reference: string | null;
  section_label: string | null;
  applicable: boolean;
  acceptable_topic: boolean;
  current_inputs: Record<string, number> | null;
  current_score: number | null;
  current_band: string | null;
  residual_inputs: Record<string, number> | null;
  residual_score: number | null;
  residual_band: string | null;
  nonconformity_text: string | null;
  recommendation_text: string | null;
  form_question_id: string | null;
  sort: number;
}

export async function listRiskEvaluations(assessmentId: string): Promise<RiskEvaluationRow[]> {
  return unwrap(
    supabase
      .from('risk_evaluations')
      .select(
        'id, hazard_zone, hazard_risk, nr_reference, section_label, applicable, acceptable_topic, current_inputs, current_score, current_band, residual_inputs, residual_score, residual_band, nonconformity_text, recommendation_text, form_question_id, sort',
      )
      .eq('risk_assessment_id', assessmentId)
      .order('sort'),
  );
}

// ------------------------------------------------------------------ NC e ações
export interface FindingRow {
  id: string;
  code: string | null;
  title: string;
  description: string | null;
  nr_reference: string | null;
  severity_band: string | null;
  detected_on: string;
  finding_statuses: { key: string; name: string };
  assets: { name: string; tag: string | null } | null;
}

const FINDING_COLS =
  'id, code, title, description, nr_reference, severity_band, detected_on, finding_statuses!inner(key, name), assets(name, tag)';

/**
 * NCs de UMA máquina. Aqui o limite alto é aceitável: é a lista dentro do
 * detalhe de um ativo, e não a lista canônica da organização.
 */
export async function listFindings(orgId: string, assetId?: string): Promise<FindingRow[]> {
  let q = supabase.from('findings').select(FINDING_COLS).eq('organization_id', orgId);
  if (assetId) q = q.eq('asset_id', assetId);
  return unwrap(
    q.order('detected_on', { ascending: false }).limit(200),
  ) as unknown as Promise<FindingRow[]>;
}

/**
 * Lista canônica de não conformidades, paginada no servidor.
 *
 * Mesmo defeito que o plano de ações tinha: `.limit(200)` fixo e a tela
 * contando o array recebido. Com 2.501 NCs em produção, a tela afirmava
 * "200 registros". Achado no E2E do domínio oficial.
 */
export async function searchFindings(params: {
  orgId: string;
  query?: string;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<PagedResult<FindingRow>> {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;

  let q = supabase
    .from('findings')
    .select(FINDING_COLS, { count: 'exact' })
    .eq('organization_id', params.orgId);

  const termo = (params.query ?? '').trim();
  if (termo) {
    const t = termo.replace(/[,()]/g, ' ');
    q = q.or(`title.ilike.*${t}*,code.ilike.*${t}*`);
  }

  const { data, error, count } = await q
    .order('detected_on', { ascending: false })
    .range(offset, offset + limit - 1)
    .abortSignal(params.signal as AbortSignal);

  if (error) throw toAppError(error);

  return {
    rows: (data ?? []) as unknown as FindingRow[],
    total: count ?? 0,
    fromCache: false,
    fetchedAt: Date.now(),
  };
}

export interface ActionRow {
  id: string;
  code: string | null;
  what: string;
  due_on: string | null;
  progress_pct: number;
  estimated_cost: number | null;
  action_types: { key: string; name: string; requires_work_order: boolean };
  action_statuses: { key: string; name: string; is_terminal: boolean };
}

/** Recortes do plano de ações. O nome vale para a UI e para a contagem. */
export type ActionScope = 'todas' | 'atrasadas' | 'abertas' | 'concluidas';

export interface ActionCounts {
  todas: number;
  atrasadas: number;
  abertas: number;
  concluidas: number;
}

const ACTION_COLS =
  'id, code, what, due_on, progress_pct, estimated_cost, action_types!inner(key, name, requires_work_order), action_statuses!inner(key, name, is_terminal)';

/** Hoje em ISO (data local), que é como `due_on` está gravado. */
function hoje(): string {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

/**
 * Lista paginada do plano de ações.
 *
 * "Atrasada" é prazo no passado E situação não terminal — as duas condições
 * juntas. Ação concluída depois do prazo não está atrasada: está feita.
 *
 * Antes daqui existia um `.limit(200)` fixo, e a tela dizia "200 ações" como
 * se fosse o total — com 2.500 no banco. Mentira pior que a falta: quem olhava
 * não tinha como saber que via 8% do plano.
 *
 * Busca, recorte, ordenação e contagem agora acontecem no servidor
 * (skill de arquitetura §1.5 e §6.4). O total vem do `count: 'exact'` do
 * PostgREST, não do tamanho do array recebido.
 */
export async function searchActions(params: {
  orgId: string;
  query?: string;
  scope?: ActionScope;
  limit?: number;
  offset?: number;
  signal?: AbortSignal;
}): Promise<PagedResult<ActionRow>> {
  const scope = params.scope ?? 'todas';
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;

  let q = supabase
    .from('actions')
    .select(ACTION_COLS, { count: 'exact' })
    .eq('organization_id', params.orgId);

  if (scope === 'atrasadas') {
    q = q.eq('action_statuses.is_terminal', false).lt('due_on', hoje());
  } else if (scope === 'abertas') {
    q = q.eq('action_statuses.is_terminal', false);
  } else if (scope === 'concluidas') {
    q = q.eq('action_statuses.is_terminal', true);
  }

  const termo = (params.query ?? '').trim();
  if (termo) {
    // PostgREST separa alternativas por vírgula; vírgula dentro do termo
    // quebraria a expressão, então ela sai fora.
    const t = termo.replace(/[,()]/g, ' ');
    q = q.or(`what.ilike.*${t}*,code.ilike.*${t}*`);
  }

  const { data, error, count } = await q
    .order('due_on', { nullsFirst: false })
    .range(offset, offset + limit - 1)
    .abortSignal(params.signal as AbortSignal);

  if (error) throw toAppError(error);

  return {
    rows: (data ?? []) as unknown as ActionRow[],
    total: count ?? 0,
    fromCache: false,
    fetchedAt: Date.now(),
  };
}

/**
 * Contagem de cada recorte, no servidor, com `head: true` — traz só o número,
 * sem trazer linha nenhuma.
 *
 * Existe separado da lista de propósito: contador calculado sobre a página
 * carregada é o mesmo defeito de antes, com outra roupa.
 */
export async function countActions(orgId: string, signal?: AbortSignal): Promise<ActionCounts> {
  const base = () =>
    supabase
      .from('actions')
      .select('id, action_statuses!inner(is_terminal)', { count: 'exact', head: true })
      .eq('organization_id', orgId);

  const [todas, atrasadas, abertas, concluidas] = await Promise.all([
    base().abortSignal(signal as AbortSignal),
    base()
      .eq('action_statuses.is_terminal', false)
      .lt('due_on', hoje())
      .abortSignal(signal as AbortSignal),
    base().eq('action_statuses.is_terminal', false).abortSignal(signal as AbortSignal),
    base().eq('action_statuses.is_terminal', true).abortSignal(signal as AbortSignal),
  ]);

  const err = todas.error ?? atrasadas.error ?? abertas.error ?? concluidas.error;
  if (err) throw toAppError(err);

  return {
    todas: todas.count ?? 0,
    atrasadas: atrasadas.count ?? 0,
    abertas: abertas.count ?? 0,
    concluidas: concluidas.count ?? 0,
  };
}

export async function listActionTypes(): Promise<
  { id: string; key: string; name: string; requires_work_order: boolean }[]
> {
  return unwrap(
    supabase.from('action_types').select('id, key, name, requires_work_order').order('sort'),
  );
}

// ------------------------------------------------------------------ anexos
export async function signedUrl(
  bucket: string,
  path: string,
  seconds = 300,
): Promise<string | null> {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, seconds);
  if (error) return null;
  return data?.signedUrl ?? null;
}

export async function listAttachments(
  entityType: string,
  entityId: string,
): Promise<{ id: string; storage_path: string; thumbnail_path: string | null; role: string }[]> {
  const rows = await unwrap(
    supabase
      .from('attachment_links')
      .select('role, attachments!inner(id, storage_path, thumbnail_path)')
      .eq('entity_type', entityType)
      .eq('entity_id', entityId)
      .order('sort'),
  );
  type Row = { role: string; attachments: { id: string; storage_path: string; thumbnail_path: string | null } };
  return (rows as unknown as Row[]).map((r) => ({
    id: r.attachments.id,
    storage_path: r.attachments.storage_path,
    thumbnail_path: r.attachments.thumbnail_path,
    role: r.role,
  }));
}

// ------------------------------------------------------------------ painel
/**
 * O painel inteiro numa chamada.
 *
 * `app_dashboard` soma no banco e devolve um objeto pequeno. A alternativa
 * seria treze consultas, ou baixar 2.500 ações para somar no navegador — que
 * é o defeito que já corrigimos na lista do plano de ações.
 */
export interface FiltroPainel {
  unidade?: string | null;
  setor?: string | null;
  de?: string | null;
  ate?: string | null;
  situacao?: string | null;
}

export interface Painel {
  gerado_em: string;
  hoje: string;
  filtro: Record<string, string | null>;
  kpis: {
    ativos: number;
    ativos_sem_avaliacao: number;
    ncs_abertas: number;
    acoes_atrasadas: number;
    acoes_vencendo: number;
    acoes_concluidas: number;
    acoes_total: number;
    documentos_pendentes: number;
  };
  acoes_por_situacao: Partial<
    Record<'atrasadas' | 'vencendo' | 'no_prazo' | 'concluidas' | 'sem_prazo', number>
  >;
  prazos: Partial<Record<'atrasadas' | 'ate_7_dias' | 'ate_30_dias' | 'depois', number>>;
  por_unidade: Array<{
    id: string;
    name: string;
    ativos: number;
    acoes_abertas: number;
    ncs_abertas: number;
  }>;
  por_setor: Array<{
    id: string;
    name: string;
    unidade: string;
    acoes_abertas: number;
    ncs_abertas: number;
  }>;
  ativos_com_pendencias: Array<{
    id: string;
    name: string;
    tag: string | null;
    ncs_abertas: number;
    acoes_abertas: number;
    acoes_atrasadas: number;
  }>;
  evolucao: {
    confiavel: boolean;
    motivo: string | null;
    series: Array<{
      mes: string;
      acoes_criadas: number;
      acoes_concluidas: number;
      ncs_criadas: number;
    }>;
  };
  custos: {
    total_acoes: number;
    com_previsto: number;
    com_realizado: number;
    cobertura_previsto: number;
    cobertura_realizado: number;
    previsto: number;
    realizado: number;
    confiavel: boolean;
  };
  risco: {
    avaliacoes_calculadas: number;
    bandas_importadas: Array<{ banda: string; total: number }>;
  };
}

export async function carregarPainel(orgId: string, filtro: FiltroPainel = {}): Promise<Painel> {
  const { data, error } = await supabase.rpc('app_dashboard', {
    p_org: orgId,
    p_unit: filtro.unidade ?? null,
    p_sector: filtro.setor ?? null,
    p_de: filtro.de ?? null,
    p_ate: filtro.ate ?? null,
    p_situacao: filtro.situacao ?? null,
  });
  if (error) throw toAppError(error);
  return data as unknown as Painel;
}

// ------------------------------------------------------------------ jornada
export interface Jornada {
  gerado_em: string;
  inventario: { total: number; sem_setor: number; sem_tag: number; sem_foto: number; desativados: number };
  inspecao: {
    total: number;
    em_andamento: number;
    finalizadas: number;
    canceladas: number;
    ativos_inspecionados: number;
    ativos_sem_inspecao: number;
  };
  apreciacao: {
    total: number;
    aprovadas: number;
    rascunho: number;
    linhas_avaliadas: number;
    ativos_sem_apreciacao: number;
    metodologias_aprovadas: number;
  };
  nao_conformidades: {
    total: number;
    abertas: number;
    encerradas: number;
    sem_acao: number;
    origem_importacao: number;
    propostas_aguardando: number;
  };
  plano_acoes: {
    total: number;
    abertas: number;
    atrasadas: number;
    concluidas: number;
    sem_prazo: number;
    vinculadas_a_nc: number;
  };
  execucao: { total: number; abertas: number; executadas: number; atrasadas: number };
  validacao: { total: number; aprovadas: number; concluidas_sem_validacao: number };
  documentos: { total: number; emitidos: number; sem_versao: number; vencidos: number };
  monitoramento: { total: number; vencidos: number; proximos: number };
}

export async function carregarJornada(orgId: string): Promise<Jornada> {
  const { data, error } = await supabase.rpc('app_jornada', { p_org: orgId });
  if (error) throw toAppError(error);
  return data as unknown as Jornada;
}

export interface JornadaDoAtivo {
  asset_id: string;
  cadastro: {
    criado_em: string;
    tem_tag: boolean;
    tem_setor: boolean;
    tem_foto: boolean;
    situacao: string | null;
  };
  inspecao: { total: number; ultima: string | null; em_andamento: number; finalizadas: number };
  apreciacao: { total: number; aprovadas: number; ultima: string | null; perigos: number };
  nao_conformidades: {
    total: number;
    abertas: number;
    ultima: string | null;
    propostas_aguardando: number;
  };
  acoes: {
    total: number;
    abertas: number;
    atrasadas: number;
    concluidas: number;
    proximo_prazo: string | null;
  };
  validacao: { total: number; aprovadas: number };
  documentos: { total: number; emitidos: number };
}

export async function carregarJornadaDoAtivo(assetId: string): Promise<JornadaDoAtivo> {
  const { data, error } = await supabase.rpc('app_jornada_ativo', { p_asset: assetId });
  if (error) throw toAppError(error);
  return data as unknown as JornadaDoAtivo;
}

// ------------------------------------------------------------------ documentos
export type Disponibilidade = 'disponivel' | 'sem_dados' | 'bloqueado';

export interface TipoDeDocumento {
  key: string;
  name: string;
  sort: number;
  tipo_id: string;
  documento_id: string | null;
  number: string | null;
  title: string | null;
  status: string | null;
  valid_until: string | null;
  versao_id: string | null;
  revision: string | null;
  state: 'draft' | 'review' | 'final' | 'superseded' | null;
  finalized_at: string | null;
  page_count: number | null;
  validation_code: string | null;
  bytes: number | null;
  responsavel: string | null;
  revisoes: number;
  alerta: string | null;
  disponibilidade: Disponibilidade;
  motivo: string | null;
  exige_rt: boolean;
}

export interface CentralDeDocumentos {
  gerado_em: string;
  base: {
    ativos: number;
    acoes: number;
    ncs: number;
    inspecoes_finalizadas: number;
    apreciacoes_aprovadas: number;
    apreciacoes: number;
    validacoes: number;
    medicoes: number;
    metodologias: number;
  };
  tipos: TipoDeDocumento[];
  anexos: { arts: number; anexos_de_documento: number; assinaturas: number };
}

export async function carregarDocumentos(orgId: string): Promise<CentralDeDocumentos> {
  const { data, error } = await supabase.rpc('app_documentos', { p_org: orgId });
  if (error) throw toAppError(error);
  return data as unknown as CentralDeDocumentos;
}

// ------------------------------------------------------ documento: plano de ações
/**
 * As linhas do Plano de Ações, buscadas EM PÁGINAS.
 *
 * Duas regras que este bloco existe para cumprir:
 *
 *  1. estimar antes de gerar. `estimarPlanoDeAcoes` conta no servidor e
 *     devolve também a previsão de páginas — ninguém deve descobrir que o
 *     documento tem 60 páginas depois de esperar a geração;
 *  2. nunca selecionar tudo por acidente. A busca só acontece quando a tela
 *     pede, com o recorte explícito, e vem em páginas de 500 com progresso.
 */
export interface FiltroDocumento {
  unidade?: string | null;
  setor?: string | null;
  scope?: ActionScope;
  de?: string | null;
  ate?: string | null;
}

// O formato da linha é do DOCUMENTO, não do repositório: quem manda é o
// módulo que também roda na Edge Function.
import type { LinhaDoPlano } from '@/domain/pdf/plano-de-acoes';
export type { LinhaDoPlano };

const PLANO_COLS =
  'id, code, what, due_on, progress_pct, estimated_cost, ' +
  'action_types!inner(name), action_statuses!inner(name, is_terminal), ' +
  'assets(name, tag, units(name), sectors(name))';

interface PlanoRaw {
  id: string;
  code: string | null;
  what: string;
  due_on: string | null;
  progress_pct: number;
  estimated_cost: number | null;
  action_types: { name: string };
  action_statuses: { name: string; is_terminal: boolean };
  assets: {
    name: string;
    tag: string | null;
    units: { name: string } | null;
    sectors: { name: string } | null;
  } | null;
}

function consultaDoPlano(orgId: string, f: FiltroDocumento) {
  let q = supabase.from('actions').select(PLANO_COLS, { count: 'exact' }).eq('organization_id', orgId);

  const scope = f.scope ?? 'todas';
  if (scope === 'atrasadas') q = q.eq('action_statuses.is_terminal', false).lt('due_on', hoje());
  else if (scope === 'abertas') q = q.eq('action_statuses.is_terminal', false);
  else if (scope === 'concluidas') q = q.eq('action_statuses.is_terminal', true);

  if (f.de) q = q.gte('due_on', f.de);
  if (f.ate) q = q.lte('due_on', f.ate);
  if (f.unidade) q = q.eq('assets.unit_id', f.unidade);
  if (f.setor) q = q.eq('assets.sector_id', f.setor);

  return q;
}

/** Linhas por página do documento — medido no motor, não chutado. */
export const LINHAS_POR_PAGINA = 24;

export async function estimarPlanoDeAcoes(
  orgId: string,
  filtro: FiltroDocumento,
): Promise<{ registros: number; paginas: number }> {
  const { count, error } = await consultaDoPlano(orgId, filtro).range(0, 0);
  if (error) throw toAppError(error);
  const registros = count ?? 0;
  // capa + sumário + identificação + resumo, e daí a tabela
  const paginas = registros === 0 ? 0 : 3 + Math.ceil(registros / LINHAS_POR_PAGINA);
  return { registros, paginas };
}

export async function carregarPlanoDeAcoes(
  orgId: string,
  filtro: FiltroDocumento,
  opcoes?: { aoProgredir?: (carregadas: number, total: number) => void; maximo?: number },
): Promise<LinhaDoPlano[]> {
  const PAGINA = 500;
  const linhas: LinhaDoPlano[] = [];
  let offset = 0;
  let total = 0;

  for (;;) {
    const { data, count, error } = await consultaDoPlano(orgId, filtro)
      .order('due_on', { ascending: true, nullsFirst: false })
      .range(offset, offset + PAGINA - 1);
    if (error) throw toAppError(error);
    total = count ?? 0;

    for (const r of (data ?? []) as unknown as PlanoRaw[]) {
      linhas.push({
        id: r.id,
        code: r.code,
        what: r.what,
        due_on: r.due_on,
        progress_pct: r.progress_pct,
        estimated_cost: r.estimated_cost,
        tipo: r.action_types.name,
        situacao: r.action_statuses.name,
        terminal: r.action_statuses.is_terminal,
        ativo: r.assets?.name ?? null,
        tag: r.assets?.tag ?? null,
        unidade: r.assets?.units?.name ?? null,
        setor: r.assets?.sectors?.name ?? null,
      });
    }

    opcoes?.aoProgredir?.(linhas.length, total);
    offset += PAGINA;
    if (linhas.length >= total || (data ?? []).length === 0) break;
    if (opcoes?.maximo && linhas.length >= opcoes.maximo) break;
  }

  return linhas;
}

// ------------------------------------------------- documento oficial: fila e histórico
/**
 * A emissão oficial é ASSÍNCRONA, e isso não é detalhe de implementação — é o
 * que a tela precisa contar.
 *
 * Medido: um plano de 2 500 ações custa 28 s de CPU para desenhar, e 9 967
 * ações passam de dois minutos. Nenhum navegador deve ficar preso nisso, e
 * nenhuma Edge Function aguenta (o teto lá é 2 s). Então quem pede recebe um
 * NÚMERO DE TRABALHO, e o estado mora no banco: o usuário pode fechar a página,
 * voltar depois e encontrar o documento pronto.
 *
 * A prévia continua síncrona e no navegador — ela é conferência, não documento.
 */
export type EstadoDoJob = 'queued' | 'running' | 'done' | 'failed' | 'cancelled';

export interface JobDoDocumento {
  id: string;
  estado: EstadoDoJob;
  progresso: number;
  paginas_feitas: number | null;
  paginas_totais: number | null;
  tentativa: number;
  erro: string | null;
  comecou_em: string | null;
  terminou_em: string | null;
  ultimo_sinal: string | null;
  /** lease vencido: o worker que pegou este trabalho não dá mais sinal */
  parado: boolean;
}

export interface EstadoDoDocumento {
  versao_id: string;
  situacao_da_versao: 'draft' | 'review' | 'final' | 'superseded';
  revisao: number;
  numero: string | null;
  paginas: number | null;
  bytes: number | null;
  sha256: string | null;
  codigo_validacao: string | null;
  storage_path: string | null;
  emitido_em: string | null;
  job: JobDoDocumento | null;
}

export interface VersaoNoHistorico {
  versao_id: string;
  documento_id: string;
  number: string | null;
  revision: number;
  state: 'draft' | 'review' | 'final' | 'superseded';
  finalized_at: string | null;
  page_count: number | null;
  bytes: number | null;
  sha256: string | null;
  validation_code: string | null;
  storage_path: string | null;
  criado_em: string;
  responsavel: string | null;
  total_acoes: number | null;
  recorte: string | null;
  e_atual: boolean;
}

/**
 * Congela o conteúdo no servidor e devolve a versão em rascunho.
 *
 * O snapshot é a razão de o documento oficial não aceitar nada do navegador:
 * as linhas ficam gravadas no banco no momento da preparação, e é delas que o
 * worker desenha. Um cliente adulterado não tem por onde inventar números.
 */
export async function prepararPlanoDeAcoes(
  orgId: string,
  filtro: FiltroDocumento,
): Promise<{ versao_id: string; documento_id: string; revisao: number; total: number }> {
  const { data, error } = await supabase.rpc('app_preparar_plano_acoes', {
    p_org: orgId,
    p_unit: filtro.unidade ?? null,
    p_sector: filtro.setor ?? null,
    p_scope: filtro.scope ?? 'todas',
  });
  if (error) throw toAppError(error);
  return data as unknown as { versao_id: string; documento_id: string; revisao: number; total: number };
}

/**
 * Põe o trabalho na fila. A Edge Function autentica, autoriza e enfileira —
 * ela não desenha.
 */
export async function enfileirarDocumento(
  versaoId: string,
): Promise<{ job_id?: string; estado?: EstadoDoJob; ja_estava_final?: boolean }> {
  const { data, error } = await supabase.functions.invoke('finalizar-plano-acoes', {
    body: { versao_id: versaoId },
  });
  if (error) throw toAppError(error);
  return data as { job_id?: string; estado?: EstadoDoJob; ja_estava_final?: boolean };
}

export async function estadoDoDocumento(versaoId: string): Promise<EstadoDoDocumento> {
  const { data, error } = await supabase.rpc('app_estado_do_documento', { p_versao: versaoId });
  if (error) throw toAppError(error);
  return data as unknown as EstadoDoDocumento;
}

export async function cancelarDocumento(versaoId: string): Promise<{ cancelado: boolean }> {
  const { data, error } = await supabase.rpc('app_cancelar_documento', { p_versao: versaoId });
  if (error) throw toAppError(error);
  return data as unknown as { cancelado: boolean };
}

export async function historicoDeDocumentos(
  orgId: string,
  tipo: string,
): Promise<VersaoNoHistorico[]> {
  const { data, error } = await supabase.rpc('app_historico_documento', {
    p_org: orgId,
    p_tipo: tipo,
  });
  if (error) throw toAppError(error);
  return (data ?? []) as unknown as VersaoNoHistorico[];
}

/**
 * Baixa os bytes do documento emitido.
 *
 * O bucket é PRIVADO: não existe URL pública, e a leitura passa pela política
 * que confere a organização. Quem não é da organização recebe recusa, não
 * arquivo.
 *
 * Devolve os bytes, e não uma URL, de propósito: com os bytes em mão a mesma
 * função serve para visualizar, baixar e imprimir, e o visualizador não precisa
 * de uma segunda viagem à rede.
 */
export async function baixarDocumentoEmitido(storagePath: string): Promise<Uint8Array> {
  const { data, error } = await supabase.storage.from('documents').download(storagePath);
  if (error) throw toAppError(error);
  return new Uint8Array(await data.arrayBuffer());
}

/**
 * Existe worker vivo para atender a fila?
 *
 * POR QUE A TELA PRECISA PERGUNTAR: sem worker no ar, apertar "Emitir
 * documento oficial" põe o pedido numa fila que ninguém atende. O usuário fica
 * olhando "Aguardando na fila" indefinidamente, sem nenhuma mensagem que
 * explique o que houve — e a explicação existe: não há processador.
 *
 * A consulta é barata e o resultado governa um botão. Visualizar, baixar e
 * imprimir documentos JÁ EMITIDOS não dependem disto: eles não passam pelo
 * worker.
 */
export interface SaudeDaGeracao {
  disponivel: boolean;
  ultimo_sinal: string | null;
  tolerancia_segundos: number;
}

export async function saudeDaGeracao(): Promise<SaudeDaGeracao> {
  const { data, error } = await supabase.rpc('app_saude_da_geracao');
  if (error) throw toAppError(error);
  return data as unknown as SaudeDaGeracao;
}

// ------------------------------------------------- NCs com contexto da máquina
/**
 * A lista geral de não conformidades, com de onde cada uma veio.
 *
 * A versão anterior mostrava cinco colunas e a linha não levava a lugar nenhum:
 * o gestor via o problema e não chegava nele. Esta traz máquina, TAG, unidade,
 * setor, origem, responsável, a ação vinculada com o prazo DELA e a miniatura
 * da foto de capa.
 *
 * Filtro, ordenação, contagem exata e paginação acontecem NO SERVIDOR. Com
 * 2.501 NCs, baixar tudo para contar no navegador trava o celular do técnico.
 */
export interface NaoConformidadeNaLista {
  id: string;
  codigo: string | null;
  titulo: string;
  resumo: string;
  origem: string;
  /** banda importada, na escala de origem — nunca convertida em HRN */
  risco: string | null;
  identificada_em: string;
  situacao: string;
  situacao_chave: string;
  maquina_id: string | null;
  maquina: string | null;
  tag: string | null;
  unidade: string | null;
  setor: string | null;
  responsavel: string | null;
  /** caminho da MINIATURA no bucket privado; nulo quando não há foto */
  miniatura: string | null;
  /** o prazo é da AÇÃO. Sem ação, não há prazo a mostrar */
  acao: { id: string; codigo: string | null; prazo: string | null } | null;
}

export interface FiltroDeNaoConformidades {
  busca?: string;
  unidade?: string | null;
  setor?: string | null;
  maquina?: string | null;
  situacao?: string | null;
  origem?: string | null;
  responsavel?: string | null;
  risco?: string | null;
  de?: string | null;
  ate?: string | null;
  /** 'com' | 'sem' | null */
  acao?: string | null;
}

export async function buscarNaoConformidades(
  orgId: string,
  filtro: FiltroDeNaoConformidades,
  pagina: { limite: number; offset: number },
): Promise<{ total: number; linhas: NaoConformidadeNaLista[] }> {
  const { data, error } = await supabase.rpc('app_nao_conformidades', {
    p_org: orgId,
    p_busca: filtro.busca || null,
    p_unidade: filtro.unidade || null,
    p_setor: filtro.setor || null,
    p_maquina: filtro.maquina || null,
    p_situacao: filtro.situacao || null,
    p_origem: filtro.origem || null,
    p_responsavel: filtro.responsavel || null,
    p_risco: filtro.risco || null,
    p_de: filtro.de || null,
    p_ate: filtro.ate || null,
    p_acao: filtro.acao || null,
    p_limite: pagina.limite,
    p_offset: pagina.offset,
  });
  if (error) throw toAppError(error);
  return data as unknown as { total: number; linhas: NaoConformidadeNaLista[] };
}

export interface OpcoesDeFiltroNC {
  situacoes: Array<{ chave: string; rotulo: string; total: number }>;
  origens: Array<{ chave: string; total: number }>;
  riscos: Array<{ chave: string; total: number }>;
  responsaveis: Array<{ id: string; nome: string; total: number }>;
  com_acao: { com: number; sem: number };
}

/**
 * As opções vêm do que EXISTE na organização, não de uma lista escrita à mão.
 *
 * Uma lista fixa no código vira mentira no dia em que um cliente usa outro
 * vocabulário — e a ICASA usa: as bandas dela vieram da planilha.
 */
export async function opcoesDeFiltroNC(orgId: string): Promise<OpcoesDeFiltroNC> {
  const { data, error } = await supabase.rpc('app_filtros_nao_conformidades', { p_org: orgId });
  if (error) throw toAppError(error);
  return data as unknown as OpcoesDeFiltroNC;
}

// ------------------------------------------------- documentos por documento lógico
/**
 * A Central deixa de listar REVISÃO como se fosse DOCUMENTO.
 *
 * Sete revisões do mesmo Plano de Ações apareciam como sete documentos, e quem
 * olhava não sabia qual estava valendo. Documento lógico é o que tem número;
 * revisão é o que tem bytes, hash e código.
 */
export interface RevisaoVigente {
  versao_id: string;
  revisao: number;
  situacao: 'draft' | 'review' | 'final' | 'superseded';
  emitido_em: string | null;
  paginas: number | null;
  bytes: number | null;
  sha256: string | null;
  codigo_validacao: string | null;
  storage_path: string | null;
}

export interface DocumentoLogico {
  documento_id: string;
  numero: string | null;
  titulo: string | null;
  tipo: string;
  tipo_nome: string;
  recorte: string | null;
  total_registros: number | null;
  revisoes: number;
  responsavel: string | null;
  vigente: RevisaoVigente;
}

export interface RevisaoDoDocumento extends RevisaoVigente {
  /** código de formato anterior: continua valendo, e é identificado */
  codigo_legado: boolean;
  responsavel: string | null;
  recorte: string | null;
  total_registros: number | null;
  vigente: boolean;
  /** a revisão que substituiu esta, quando houver */
  substituida_por: number | null;
}

export async function documentosDoTipo(
  orgId: string,
  tipo: string,
  opcoes?: { busca?: string; situacao?: string | null; limite?: number; offset?: number },
): Promise<{ total: number; documentos: DocumentoLogico[] }> {
  const { data, error } = await supabase.rpc('app_documentos_do_tipo', {
    p_org: orgId,
    p_tipo: tipo,
    p_busca: opcoes?.busca || null,
    p_situacao: opcoes?.situacao || null,
    p_limite: opcoes?.limite ?? 25,
    p_offset: opcoes?.offset ?? 0,
  });
  if (error) throw toAppError(error);
  return data as unknown as { total: number; documentos: DocumentoLogico[] };
}

/** Carregadas só ao expandir: um documento com trinta revisões não pesa na lista. */
export async function revisoesDoDocumento(documentoId: string): Promise<RevisaoDoDocumento[]> {
  const { data, error } = await supabase.rpc('app_revisoes_do_documento', {
    p_documento: documentoId,
  });
  if (error) throw toAppError(error);
  return (data ?? []) as unknown as RevisaoDoDocumento[];
}

export interface VersaoCompleta extends RevisaoDoDocumento {
  documento_id: string;
  numero: string | null;
  titulo: string | null;
  tipo: string;
  tipo_nome: string;
  organizacao: string;
}

export async function versaoDoDocumento(versaoId: string): Promise<VersaoCompleta> {
  const { data, error } = await supabase.rpc('app_versao_do_documento', { p_versao: versaoId });
  if (error) throw toAppError(error);
  return data as unknown as VersaoCompleta;
}

// ------------------------------------------------------------- dossiê da máquina
/**
 * O dossiê de uma máquina: cabeçalho e contagem de cada aba, numa chamada.
 *
 * A página de ativo tinha 136 linhas — a menor do sistema, e deveria ser a
 * maior. Não mostrava ações, ordens de serviço, validações nem documentos. Quem
 * abria uma máquina não conseguia responder "e agora?".
 *
 * As contagens vêm juntas para a tela poder escrever "Ações (12)" sem carregar
 * as doze. As linhas de cada aba vêm em `abaDoDossie`, quando a aba abre.
 */
export interface DossieDoAtivo {
  identificacao: {
    id: string;
    nome: string;
    tag: string | null;
    codigo_alternativo: string | null;
    fabricante: string | null;
    modelo: string | null;
    numero_serie: string | null;
    ano: number | null;
    unidade: string | null;
    unidade_id: string | null;
    setor: string | null;
    setor_id: string | null;
    criticidade: string | null;
    situacao: string | null;
    tem_qr: boolean;
  };
  foto_capa: string | null;
  inspecao: {
    nunca_inspecionada: boolean;
    ultima: { id: string; data: string | null; situacao: string; concluida_em: string | null } | null;
    /** id da inspeção começada e não finalizada: decide entre Iniciar e Continuar */
    em_andamento: string | null;
  };
  pendencias: {
    ncs_abertas: number;
    acoes_abertas: number;
    acoes_atrasadas: number;
    ordens_abertas: number;
  };
  abas: {
    fotos: number;
    inspecoes: number;
    riscos: number;
    ncs: number;
    acoes: number;
    ordens: number;
    validacoes: number;
  };
  proxima_acao: { texto: string; rotulo: string; rota: string };
}

export async function dossieDoAtivo(ativoId: string): Promise<DossieDoAtivo> {
  const { data, error } = await supabase.rpc('app_dossie_do_ativo', { p_ativo: ativoId });
  if (error) throw toAppError(error);
  return data as unknown as DossieDoAtivo;
}

export type AbaDoDossie = 'acoes' | 'ordens' | 'inspecoes' | 'riscos' | 'validacoes';

/** As linhas de UMA aba. Uma máquina com 50 ações não paga isso no resumo. */
export async function abaDoDossie(
  ativoId: string,
  aba: AbaDoDossie,
  limite = 50,
): Promise<Record<string, unknown>[]> {
  const { data, error } = await supabase.rpc('app_dossie_aba', {
    p_ativo: ativoId,
    p_aba: aba,
    p_limite: limite,
  });
  if (error) throw toAppError(error);
  return (data ?? []) as unknown as Record<string, unknown>[];
}

// ------------------------------------------------------- ciclo da ação
/**
 * O progresso de uma ação, COM A ORIGEM DELE.
 *
 * Auditado antes de mudar qualquer coisa: as 2.500 ações da ICASA estão
 * coerentes (2.193 pendentes com 2% de média, 307 concluídas todas em 100%,
 * nenhuma combinação impossível). Quem produzia número incoerente era o
 * gerador de dado de teste.
 *
 * O que faltava não era validação, era PROCEDÊNCIA: 60% não dizia se alguém
 * arrastou um controle, se veio da planilha, ou se a execução chegou à metade.
 * Ação com marcos tem progresso derivado deles; ação sem nenhum marco — todas
 * as importadas — mantém o valor guardado, identificado como informado.
 */
export type MarcoDaAcao =
  | 'criada'
  | 'planejada'
  | 'os_aberta'
  | 'execucao_iniciada'
  | 'execucao_concluida'
  | 'aguardando_validacao'
  | 'validada';

export interface ProgressoDaAcao {
  valor: number;
  origem: 'marcos' | 'informado';
  valor_guardado: number;
  marcos: Array<{
    marco: MarcoDaAcao;
    peso: number;
    ocorrido_em: string;
    por: string | null;
    notas: string | null;
  }>;
  exige_os: boolean;
  validada: boolean;
  rotulo: string;
  /** mostradas, nunca corrigidas em silêncio */
  incoerencias: string[];
}

export async function progressoDaAcao(acaoId: string): Promise<ProgressoDaAcao> {
  const { data, error } = await supabase.rpc('app_progresso_da_acao', { p_acao: acaoId });
  if (error) throw toAppError(error);
  return data as unknown as ProgressoDaAcao;
}

export async function registrarMarco(
  acaoId: string,
  marco: MarcoDaAcao,
  notas?: string,
): Promise<ProgressoDaAcao> {
  const { data, error } = await supabase.rpc('app_registrar_marco', {
    p_acao: acaoId,
    p_marco: marco,
    p_notas: notas ?? null,
  });
  if (error) throw toAppError(error);
  return data as unknown as ProgressoDaAcao;
}

/**
 * Pode validar? E, quando não, POR QUÊ.
 *
 * Botão desabilitado sem explicação faz o usuário achar que o sistema quebrou.
 */
export interface PodeValidar {
  pode: boolean;
  ja_validada: boolean;
  exige_os?: boolean;
  bloqueios: string[];
}

export async function podeValidar(acaoId: string): Promise<PodeValidar> {
  const { data, error } = await supabase.rpc('app_pode_validar', { p_acao: acaoId });
  if (error) throw toAppError(error);
  return data as unknown as PodeValidar;
}

export interface OrdemDeServico {
  id: string;
  numero: string | null;
  tipo: string | null;
  descricao: string | null;
  responsavel: string | null;
  aberta_em: string | null;
  prazo: string | null;
  executada_em: string | null;
  custo_mao_de_obra: number | null;
  custo_pecas: number | null;
  custo_total: number;
  situacao: string | null;
  situacao_chave: string | null;
  encerrada: boolean;
}

export async function ordensDaAcao(acaoId: string): Promise<OrdemDeServico[]> {
  const { data, error } = await supabase.rpc('app_ordens_da_acao', { p_acao: acaoId });
  if (error) throw toAppError(error);
  return (data ?? []) as unknown as OrdemDeServico[];
}

// ------------------------------------------------------------- galeria de fotos
/**
 * A galeria de uma entidade, por categoria.
 *
 * A MESMA imagem pode estar ligada à máquina, à inspeção, ao risco, à NC, à
 * ação, à OS e à validação sem duplicar bytes: `attachments` guarda o arquivo
 * uma vez (com sha256) e `attachment_links` faz os vínculos. A legenda é do
 * VÍNCULO — a mesma foto pode ser "risco" no dossiê e "antes" na evidência.
 */
export interface FotoDaGaleria {
  link_id: string;
  anexo_id: string;
  categoria: string;
  papel_original: string;
  legenda: string | null;
  caminho: string;
  miniatura: string;
  largura: number | null;
  altura: number | null;
  bytes: number | null;
  sha256: string | null;
  enviada_em: string;
  autor: string | null;
  ordem: number;
}

export interface CategoriaDeFoto {
  chave: string;
  rotulo: string;
  ordem: number;
  ajuda: string;
}

export async function carregarGaleria(
  tipo: string,
  entidadeId: string,
): Promise<{ categorias: CategoriaDeFoto[]; fotos: FotoDaGaleria[] }> {
  // com prazo: sem rede, a galeria falha e a tela diz isso. Sem o prazo ela
  // ficava em "Carregando as fotos…" para sempre no meio da inspeção de campo.
  const { data, error } = await comPrazo(
    Promise.resolve(supabase.rpc('app_galeria', { p_tipo: tipo, p_entidade: entidadeId })),
    PRAZO_DE_LEITURA,
  );
  if (error) throw toAppError(error);
  return data as unknown as { categorias: CategoriaDeFoto[]; fotos: FotoDaGaleria[] };
}

export async function editarFoto(
  linkId: string,
  mudancas: { legenda?: string; categoria?: string },
): Promise<void> {
  const { error } = await supabase.rpc('app_editar_foto', {
    p_link: linkId,
    p_legenda: mudancas.legenda ?? null,
    p_categoria: mudancas.categoria ?? null,
  });
  if (error) throw toAppError(error);
}

/** Trocar a capa não apaga a anterior: ela volta para a galeria geral. */
export async function definirCapa(ativoId: string, linkId: string): Promise<void> {
  const { error } = await supabase.rpc('app_definir_capa', { p_ativo: ativoId, p_link: linkId });
  if (error) throw toAppError(error);
}

/** Remove o VÍNCULO; o arquivo sobrevive enquanto outra entidade o usar. */
export async function removerFoto(linkId: string): Promise<{ vinculos_restantes: number }> {
  const { data, error } = await supabase.rpc('app_remover_foto', { p_link: linkId });
  if (error) throw toAppError(error);
  return data as unknown as { vinculos_restantes: number };
}

/**
 * O vocabulário de categorias, direto do banco.
 *
 * Não se copia a lista para dentro do JavaScript: na primeira divergência a
 * foto entraria com uma chave que nenhuma seção da galeria exibe.
 */
export async function categoriasDeFoto(): Promise<CategoriaDeFoto[]> {
  const { data, error } = await supabase.rpc('app_categorias_de_foto');
  if (error) throw toAppError(error);
  return (data as unknown as CategoriaDeFoto[]) ?? [];
}

// ------------------------------------------------------- inspeção em campo
/**
 * A entrada da inspeção, a execução e a revisão das propostas.
 *
 * Tudo aqui é leitura ou revisão. O que a inspeção ESCREVE (começar, responder,
 * finalizar) passa pela fila de `sync.ts`, nunca por estas funções — é o que
 * garante que o galpão sem sinal continue funcionando.
 */

export interface MaquinaDaBusca {
  id: string;
  nome: string;
  tag: string | null;
  inventario: string | null;
  unidade: string | null;
  setor: string | null;
  miniatura: string | null;
  inspecao_em_andamento: string | null;
  ncs_abertas: number;
}

export async function buscarMaquina(orgId: string, busca: string): Promise<MaquinaDaBusca[]> {
  const { data, error } = await supabase.rpc('app_buscar_maquina', {
    p_org: orgId,
    p_busca: busca || null,
  });
  if (error) throw toAppError(error);
  return (data as unknown as MaquinaDaBusca[]) ?? [];
}

export async function campanhaDeChecklist(orgId: string): Promise<string> {
  const { data, error } = await supabase.rpc('app_campanha_de_checklist', { p_org: orgId });
  if (error) throw toAppError(error);
  return data as unknown as string;
}

export interface InspecaoCarregada {
  inspecao: {
    id: string;
    situacao: string;
    data: string;
    turno: string | null;
    finalizada_em: string | null;
    ativo_id: string | null;
    versao_do_formulario: string;
    campanha_id: string;
    em_andamento: boolean;
    autor: string | null;
    finalizada_por: string | null;
  };
  ativo: {
    id: string; nome: string; tag: string | null;
    unidade: string | null; setor: string | null; miniatura: string | null;
  } | null;
  respostas: Record<
    string,
    { id: string; conformidade: string | null; texto: string | null; nota: string | null; fotos: number }
  >;
  propostas: { id: string; pergunta: string; situacao: string; finding_id: string | null }[];
}

export async function carregarInspecao(runId: string): Promise<InspecaoCarregada> {
  const { data, error } = await supabase.rpc('app_inspecao', { p_run: runId });
  if (error) throw toAppError(error);
  return data as unknown as InspecaoCarregada;
}

export interface InspecaoDoAtivo {
  id: string;
  data: string;
  turno: string | null;
  situacao: string;
  em_andamento: boolean;
  respondidas: number;
  nao_conformes: number;
  nao_verificados: number;
  propostas_pendentes: number;
  autor: string | null;
}

export async function inspecoesDoAtivo(ativoId: string, limite = 20): Promise<InspecaoDoAtivo[]> {
  const { data, error } = await supabase.rpc('app_inspecoes_do_ativo', {
    p_ativo: ativoId,
    p_limite: limite,
  });
  if (error) throw toAppError(error);
  return (data as unknown as InspecaoDoAtivo[]) ?? [];
}

export interface PropostaDeNC {
  id: string;
  situacao: 'aguardando_revisao' | 'confirmada' | 'rejeitada' | 'duplicada' | 'agrupada';
  pergunta: string;
  requisito: string | null;
  observacao: string | null;
  descricao_sugerida: string;
  descricao_revisada: string | null;
  justificativa: string | null;
  criada_em: string;
  autor: string | null;
  revisor: string | null;
  revisada_em: string | null;
  inspecao_id: string;
  resposta_id: string;
  ativo: { id: string; nome: string; tag: string | null; unidade: string | null; setor: string | null } | null;
  fotos: number;
  finding: { id: string; codigo: string } | null;
  duplicada_de: { id: string; codigo: string } | null;
}

export async function listarPropostas(
  orgId: string,
  situacao: string | null = 'aguardando_revisao',
  ativoId?: string | null,
): Promise<PropostaDeNC[]> {
  const { data, error } = await supabase.rpc('app_propostas', {
    p_org: orgId,
    p_situacao: situacao,
    p_ativo: ativoId ?? null,
  });
  if (error) throw toAppError(error);
  return (data as unknown as PropostaDeNC[]) ?? [];
}

/**
 * O formulário publicado, com cópia no aparelho.
 *
 * Quem sai para o galpão precisa do checklist inteiro ANTES de perder o sinal.
 * `loadPublishedForm` fala com o servidor; esta versão guarda o resultado no
 * mesmo cache de leitura já usado pelo resto do aplicativo e, quando a rede
 * falha, devolve a cópia local em vez de uma tela vazia.
 *
 * O cache é por CHAVE do template. Uma versão nova publicada substitui a
 * cópia na primeira vez que houver rede — nunca no meio de uma inspeção, que
 * carrega a versão que ela começou usando.
 */
export async function carregarFormularioComCache(
  orgId: string,
  templateKey: string,
): Promise<{ formulario: FormVersion; doCache: boolean }> {
  try {
    // com prazo, pelo mesmo motivo das permissões: servidor que não responde
    // não pode segurar a tela de campo — a cópia local existe para isto
    const formulario = await comPrazo(loadPublishedForm(templateKey), PRAZO_DE_LEITURA);
    await cachePut({
      entity_type: 'formulario',
      id: templateKey,
      organization_id: orgId,
      data: formulario,
      fetched_at: Date.now(),
      server_version: formulario.version,
    });
    return { formulario, doCache: false };
  } catch (erro) {
    const guardado = await cacheGet('formulario', templateKey);
    if (guardado) return { formulario: guardado.data as FormVersion, doCache: true };
    throw toAppError(erro);
  }
}

/**
 * Os vínculos do usuário, com cópia local para abrir sem rede.
 *
 * O DEFEITO QUE ISTO CORRIGE, visto em teste: com o servidor inacessível, o
 * aplicativo ficava na roda de carregamento para SEMPRE. `loadMemberships`
 * não tem prazo, e uma rede que aceita a conexão e não responde — a rede de
 * galpão típica — deixa a promessa pendurada; o `setLoading(false)` nunca
 * acontecia. Quem tinha uma inspeção começada no aparelho não conseguia nem
 * chegar até ela.
 *
 * Duas correções, as duas necessárias:
 *
 *   · PRAZO. Passou do tempo, segue com o que houver guardado. Sem isso, a
 *     tela não abre nem para mostrar um aviso.
 *   · CÓPIA LOCAL. É cache do que o servidor JÁ disse, e não concede nada: a
 *     RLS continua decidindo cada leitura e cada escrita no Postgres. Serve
 *     para a interface saber qual organização mostrar e quais botões desenhar.
 */
/**
 * Desiste depois de um tempo.
 *
 * Rede de galpão raramente "cai": ela aceita a conexão e não responde. Uma
 * promessa sem prazo nesse cenário não falha nem completa — a tela fica
 * girando para sempre, que foi exatamente o que o teste com o gateway pausado
 * mostrou. Quem tem cópia local prefere a cópia a uma espera infinita.
 */
export function comPrazo<T>(promessa: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promessa,
    new Promise<never>((_, rejeitar) =>
      setTimeout(() => rejeitar(new Error('tempo esgotado')), ms),
    ),
  ]);
}

const PRAZO_DAS_PERMISSOES = 6_000;
/** Prazo das leituras que têm cópia local para cair de volta. */
const PRAZO_DE_LEITURA = 5_000;

export async function carregarMembershipsComCache(
  usuarioId: string,
): Promise<{ memberships: Membership[]; doCache: boolean }> {
  const guardar = async (rows: Membership[]) => {
    await cachePut({
      entity_type: 'memberships',
      id: usuarioId,
      organization_id: rows[0]?.organization_id ?? usuarioId,
      data: rows,
      fetched_at: Date.now(),
      server_version: null,
    });
  };

  try {
    const rows = await comPrazo(loadMemberships(), PRAZO_DAS_PERMISSOES);
    void guardar(rows).catch(() => undefined);
    return { memberships: rows, doCache: false };
  } catch {
    const guardado = await cacheGet('memberships', usuarioId).catch(() => undefined);
    return { memberships: (guardado?.data as Membership[] | undefined) ?? [], doCache: true };
  }
}
